import { useCallback, useEffect, useState } from "react";
import {
  actionLabel,
  actionMeaning,
  agoLabel,
  detailLine,
  EMPTY_FILTER,
  filterSummary,
  getLedger,
  LEDGER_CLAIMS,
  ledgerSummary,
  refusalSentence,
  spanLabel,
  stamp,
  subjectLabel,
} from "../../api/audit";
import type { AuditRow, Ledger, LedgerFilter } from "../../api/audit";
import { download, stampedName, toCsv, type Cell } from "../../api/csv";
import { Loading } from "../Loading";

/**
 * The counterweight to full admin access, and the only view of it. Nothing on
 * this screen writes, and nothing in this console can update or delete a row.
 */

function count(value: number): string {
  return value.toLocaleString("en-IN");
}

function Card({
  label,
  value,
  unit,
  foot,
}: {
  readonly label: string;
  readonly value: string;
  readonly unit: string;
  readonly foot: string;
}) {
  return (
    <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-5 shadow-sm flex flex-col justify-between">
      <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
        {label}
      </span>
      <div className="flex items-baseline gap-2 mt-3">
        <span className="font-headline-md text-headline-md font-bold text-on-surface tracking-tight">
          {value}
        </span>
        <span className="font-body-sm text-body-sm text-on-surface-variant">{unit}</span>
      </div>
      <p className="mt-3 pt-3 border-t border-outline-variant/60 font-label-sm text-label-sm text-on-surface-variant">
        {foot}
      </p>
    </div>
  );
}

function Row({ row, onFilterSubject }: { readonly row: AuditRow; readonly onFilterSubject: (id: string) => void }) {
  const detail = detailLine(row);
  return (
    <tr className="hover:bg-surface-container-low align-top">
      <td className="py-3.5 px-5 font-label-md text-label-md text-on-surface whitespace-nowrap">
        {stamp(row.at)}
      </td>
      <td className="py-3.5 px-5 font-body-sm text-body-sm text-on-surface">{row.adminEmail}</td>
      <td className="py-3.5 px-5">
        <span className="inline-flex items-center px-2 py-0.5 rounded font-label-sm text-label-sm font-semibold bg-surface-container-high text-on-surface">
          {actionLabel(row.action)}
        </span>
        {detail === null ? null : (
          <span className="block mt-1 font-label-sm text-label-sm text-on-surface-variant">
            {detail}
          </span>
        )}
      </td>
      <td className="py-3.5 px-5 font-body-sm text-body-sm text-on-surface-variant">
        {row.subjectUserId === null ? (
          subjectLabel(row)
        ) : (
          <button
            type="button"
            onClick={() => onFilterSubject(row.subjectUserId ?? "")}
            // 24px tall at least: it drew 18px, under the pointer target floor.
            className="underline text-left inline-flex items-center min-h-[24px]"
          >
            {subjectLabel(row)}
          </button>
        )}
      </td>
      <td className="py-3.5 px-5 font-label-sm text-label-sm text-on-surface-variant">
        {row.auditId}
      </td>
    </tr>
  );
}

/**
 * The two columns the design fills that this screen leaves out, and the four
 * header buttons. Each one is a security claim, and a false security claim is
 * worse than an absent one.
 */
export function AuditLogScreen() {
  const [filter, setFilter] = useState<LedgerFilter>(EMPTY_FILTER);
  const [ledger, setLedger] = useState<Ledger | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [stamped, setStamped] = useState(0);

  const now = Date.now();

  useEffect(() => {
    let live = true;
    getLedger(filter)
      .then((view) => {
        if (!live) return;
        setLedger(view);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (!live) return;
        setError(caught instanceof Error ? caught.message : "failed");
      });
    return () => {
      live = false;
    };
  }, [filter, stamped]);

  const bySubject = useCallback((subject: string) => {
    setFilter((held) => ({ ...held, subject, page: 1 }));
  }, []);

  const exportLedger = useCallback(() => {
    setNotice(null);
    getLedger(filter, true)
      .then((whole) => {
        download(
          stampedName("prephike-admin-ledger", Date.now()),
          toCsv(
            ["row", "when (UTC)", "admin", "action", "whose record", "detail"],
            whole.rows.map((row): readonly Cell[] => [
              row.auditId,
              stamp(row.at),
              row.adminEmail,
              row.action,
              row.subjectUserId === null ? "nobody in particular" : subjectLabel(row),
              detailLine(row),
            ]),
          ),
        );
        const capped =
          whole.total > whole.exportCap
            ? ` The filter matches ${count(whole.total)}, and an export is capped at ${count(whole.exportCap)}.`
            : "";
        setNotice(
          `Exported ${count(whole.rows.length)} rows. Reading the ledger writes nothing to it, so this added no row.${capped}`,
        );
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : "failed");
      });
  }, [filter]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col gap-space-lg">
      <header className="flex flex-col xl:flex-row xl:items-end justify-between gap-4 pb-6 border-b border-outline-variant">
        <div className="flex flex-col gap-2 max-w-3xl">
          <span className="font-label-sm text-label-sm uppercase tracking-wider font-semibold text-secondary">
            Executive supervision, what an admin did and to whom
          </span>
          <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold tracking-tight">
            Audit and Security Logs
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            An append-only ledger of admin actions. A row is written the moment an admin opens one
            named candidate or reads one debrief, before any of that person's data is served. The
            row holds who, what, whose and when, and never a word of what the candidate wrote.
          </p>
        </div>
        {/* NOT BEFORE THE READ LANDS. These drew at first paint and acted
            on nothing: an export of an empty ledger, a reload of a screen
            that is already loading. */}
        <div className={ledger === null ? "hidden" : "flex flex-wrap items-center gap-2"}>
          <button
            type="button"
            onClick={exportLedger}
            disabled={ledger === null || ledger.total === 0}
            className="px-3.5 py-2 rounded-lg bg-surface-container border border-outline-variant text-on-surface hover:bg-surface-container-high disabled:opacity-50 font-body-sm text-body-sm font-semibold"
          >
            Export this ledger (CSV)
          </button>
          <button
            type="button"
            onClick={() => setStamped((one) => one + 1)}
            className="px-3.5 py-2 rounded-lg bg-surface-container border border-outline-variant text-on-surface hover:bg-surface-container-high font-body-sm text-body-sm font-semibold"
          >
            Reload
          </button>
        </div>
      </header>

      {error !== null ? (
        <p className="bg-error-container text-on-error-container border border-error/30 rounded-xl p-4 font-body-sm text-body-sm">
          {refusalSentence(error)}
        </p>
      ) : null}

      {notice !== null ? (
        <p
          role="status"
          aria-live="polite"
          className="bg-secondary-container text-on-secondary-container border border-secondary/40 rounded-xl p-4 font-body-sm text-body-sm flex items-start justify-between gap-4"
        >
          <span>{notice}</span>
          <button
            type="button"
            className="underline shrink-0 inline-flex items-center min-h-[24px]"
            onClick={() => setNotice(null)}
          >
            Dismiss
          </button>
        </p>
      ) : null}

      {ledger === null ? (
        <Loading what="Reading the audit ledger." />
      ) : (
        <>
          <p className="font-body-md text-body-md text-on-surface">{ledgerSummary(ledger.totals)}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            <Card
              label="Ledger rows"
              value={count(ledger.totals.entries)}
              unit={ledger.totals.entries === 1 ? "row" : "rows"}
              foot={spanLabel(ledger.totals)}
            />
            <Card
              label="Named one person"
              value={count(ledger.totals.identifying)}
              unit={ledger.totals.identifying === 1 ? "row" : "rows"}
              foot="The rest name nobody. Only these are a privileged read of a candidate."
            />
            <Card
              label="Candidates opened"
              value={count(ledger.totals.subjects)}
              unit={ledger.totals.subjects === 1 ? "person" : "people"}
              foot={`${count(ledger.totals.lastDay)} rows were written in the last 24 hours.`}
            />
            <Card
              label="Admins in the ledger"
              value={count(ledger.totals.admins)}
              unit={ledger.totals.admins === 1 ? "address" : "addresses"}
              foot={`Last row ${agoLabel(ledger.totals.lastAt, now)}. You are signed in as ${ledger.viewer}.`}
            />
          </div>

          <section className="bg-surface-container-lowest border border-outline-variant rounded-xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-outline-variant flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="font-headline-sm text-headline-sm font-bold text-on-surface">
                  Admin event ledger
                </h2>
                <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                  {filterSummary(filter, ledger.total)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="font-label-sm text-label-sm text-on-surface-variant">
                  <span className="sr-only">Filter by action</span>
                  <select
                    value={filter.action}
                    onChange={(event) =>
                      setFilter((held) => ({ ...held, action: event.target.value, page: 1 }))
                    }
                    className="font-body-sm text-body-sm bg-surface-container-low border border-outline-variant rounded-lg px-3 py-1.5 text-on-surface"
                  >
                    <option value="">Every action</option>
                    {ledger.actions.map((one) => (
                      <option key={one.action} value={one.action}>
                        {actionLabel(one.action)} ({count(one.count)})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="font-label-sm text-label-sm text-on-surface-variant">
                  <span className="sr-only">Filter by admin</span>
                  <select
                    value={filter.admin}
                    onChange={(event) =>
                      setFilter((held) => ({ ...held, admin: event.target.value, page: 1 }))
                    }
                    className="font-body-sm text-body-sm bg-surface-container-low border border-outline-variant rounded-lg px-3 py-1.5 text-on-surface"
                  >
                    <option value="">Every admin</option>
                    {ledger.admins.map((one) => (
                      <option key={one.adminEmail} value={one.adminEmail}>
                        {one.adminEmail} ({count(one.count)})
                      </option>
                    ))}
                  </select>
                </label>
                {filter.subject.length === 0 ? null : (
                  <button
                    type="button"
                    onClick={() => setFilter((held) => ({ ...held, subject: "", page: 1 }))}
                    className="px-3 py-1.5 rounded-lg border border-outline-variant bg-surface-container font-label-md text-label-md text-on-surface"
                  >
                    Clear the candidate filter
                  </button>
                )}
              </div>
            </div>

            {ledger.rows.length === 0 ? (
              <p className="p-8 text-center font-body-sm text-body-sm text-on-surface-variant">
                No rows match. An empty ledger means no admin has opened a named candidate yet, not
                that a row was removed: nothing here can remove one.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-surface-container-low border-b border-outline-variant font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
                    <tr>
                      <th scope="col" className="py-3 px-5 font-semibold">
                        When (UTC)
                      </th>
                      <th scope="col" className="py-3 px-5 font-semibold">
                        Admin
                      </th>
                      <th scope="col" className="py-3 px-5 font-semibold">
                        Action
                      </th>
                      <th scope="col" className="py-3 px-5 font-semibold">
                        Whose record
                      </th>
                      <th scope="col" className="py-3 px-5 font-semibold">
                        Row
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/50">
                    {ledger.rows.map((row) => (
                      <Row key={row.auditId} row={row} onFilterSubject={bySubject} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="p-4 bg-surface-container-low border-t border-outline-variant flex flex-col sm:flex-row items-center justify-between gap-3 font-label-md text-label-md text-on-surface-variant">
              <span>
                Showing {count(ledger.rows.length)} of {count(ledger.total)} matching rows, page{" "}
                {ledger.page} of {ledger.pages}
              </span>
              <span className="inline-flex gap-2">
                <button
                  type="button"
                  disabled={ledger.page <= 1}
                  onClick={() => setFilter((held) => ({ ...held, page: held.page - 1 }))}
                  className="px-2.5 py-1 rounded border border-outline-variant bg-surface-container-lowest disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={ledger.page >= ledger.pages}
                  onClick={() => setFilter((held) => ({ ...held, page: held.page + 1 }))}
                  className="px-2.5 py-1 rounded border border-outline-variant bg-surface-container-lowest disabled:opacity-40"
                >
                  Next
                </button>
              </span>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="bg-surface-container-lowest border border-outline-variant rounded-xl shadow-sm p-6">
              <h3 className="font-headline-sm text-headline-sm font-bold text-on-surface">
                What this ledger is
              </h3>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                Four claims, each true of the rows above. Anything stronger would be a security
                claim this product has not earned.
              </p>
              <dl className="mt-4 flex flex-col gap-3">
                {LEDGER_CLAIMS.map((one) => (
                  <div
                    key={one.claim}
                    className="border border-outline-variant rounded-lg p-3.5 bg-surface-container-low"
                  >
                    <dt className="font-body-sm text-body-sm font-bold text-on-surface">
                      {one.claim}
                    </dt>
                    <dd className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                      {one.body}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="bg-surface-container-lowest border border-outline-variant rounded-xl shadow-sm p-6 flex flex-col gap-5">
              <div>
                <h3 className="font-headline-sm text-headline-sm font-bold text-on-surface">
                  Who has been looked at
                </h3>
                <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                  The candidates whose records have been opened most, counted from the ledger
                  itself.
                </p>
                {ledger.subjects.length === 0 ? (
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-3">
                    Nobody has been opened yet.
                  </p>
                ) : (
                  <ul className="mt-3 flex flex-col gap-2">
                    {ledger.subjects.map((one) => (
                      <li
                        key={one.subjectUserId}
                        className="border border-outline-variant rounded-lg p-3 bg-surface-container-low flex items-start justify-between gap-3"
                      >
                        <span className="min-w-0">
                          <button
                            type="button"
                            onClick={() => bySubject(one.subjectUserId)}
                            className="font-body-sm text-body-sm font-semibold text-on-surface underline text-left truncate block min-h-[24px] leading-6 max-w-full"
                          >
                            {subjectLabel(one)}
                          </button>
                          <span className="font-label-sm text-label-sm text-on-surface-variant">
                            {count(one.debriefReads)}{" "}
                            {one.debriefReads === 1 ? "debrief read" : "debriefs read"}, last{" "}
                            {agoLabel(one.lastAt, now)}
                          </span>
                        </span>
                        <span className="font-label-md text-label-md font-semibold text-on-surface shrink-0">
                          {count(one.count)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h3 className="font-headline-sm text-headline-sm font-bold text-on-surface">
                  Who did the looking
                </h3>
                {ledger.admins.length === 0 ? (
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                    No admin has written a row yet.
                  </p>
                ) : (
                  <ul className="mt-3 flex flex-col gap-2">
                    {ledger.admins.map((one) => (
                      <li
                        key={one.adminEmail}
                        className="border border-outline-variant rounded-lg p-3 bg-surface-container-low flex items-start justify-between gap-3"
                      >
                        <span className="min-w-0">
                          <span className="font-body-sm text-body-sm font-semibold text-on-surface truncate block">
                            {one.adminEmail}
                          </span>
                          <span className="font-label-sm text-label-sm text-on-surface-variant">
                            {count(one.subjects)}{" "}
                            {one.subjects === 1 ? "candidate" : "candidates"} opened, last{" "}
                            {agoLabel(one.lastAt, now)}
                          </span>
                        </span>
                        <span className="font-label-md text-label-md font-semibold text-on-surface shrink-0">
                          {count(one.count)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h3 className="font-headline-sm text-headline-sm font-bold text-on-surface">
                  The verbs, and what each one means
                </h3>
                {ledger.actions.length === 0 ? (
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                    No action has been recorded yet.
                  </p>
                ) : (
                  <ul className="mt-3 flex flex-col gap-2">
                    {ledger.actions.map((one) => (
                      <li key={one.action} className="font-body-sm text-body-sm">
                        <span className="font-semibold text-on-surface">
                          {actionLabel(one.action)}
                        </span>
                        <span className="text-on-surface-variant">
                          {" "}
                          ({count(one.count)}): {actionMeaning(one.action)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

        </>
      )}
    </div>
  );
}
